import { useEffect, useMemo } from 'react';
import type {
	CHANGES,
	INCIDENTS,
	PROBLEMS,
} from '@atlassian/jira-servicedesk-work-category/src/common/constants.tsx';
import type { AccountId } from '@atlassian/jira-shared-types/src/general.tsx';
import { createResource, useResource } from '@atlassian/react-resource-router';
import type { JsmRepackagingBanner } from './controllers/get-data/types.tsx';
import { deleteBannerDismissState } from './services/delete-banner-dismiss-state/index.tsx';

const getDataLoader = () =>
	import(
		/*
			webpackChunkName: "async-resource-jsm-repackaging-banner",
			jiraSpaEntry: "async-resource-jsm-repackaging-banner"
		*/
		'./controllers/get-data'
	);

export const jsmRepackagingBannerResource = createResource<JsmRepackagingBanner | undefined>({
	type: 'JSM_REPACKAGING_BANNER',
	getKey: () => 'JSM_REPACKAGING_BANNER',
	getDataLoader,
	maxAge: __SERVER__ ? 0 : Number.MAX_SAFE_INTEGER,
	isBrowserOnly: true,
});

export const useJsmRepackagingBannerResource = (
	workCategory: typeof CHANGES | typeof INCIDENTS | typeof PROBLEMS,
	accountId: AccountId | null,
) => {
	const { data, loading, error, refresh } = useResource(jsmRepackagingBannerResource);

	useEffect(() => {
		if (!data && !loading && !error) {
			refresh();
		}
	}, [data, loading, refresh, error]);

	return useMemo(() => {
		if (error || loading || !data || !accountId) {
			return {
				show: false,
			} as const;
		}

		const { bannerMeta, bannerDismissState } = data;
		const workCategoryDismissState = bannerDismissState?.[workCategory];

		if (!bannerMeta.show || !(bannerMeta.type === 'info' || bannerMeta.type === 'warning')) {
			return {
				show: false,
			} as const;
		}

		if (!workCategoryDismissState) {
			return {
				...bannerDismissState,
				...bannerMeta,
				refresh,
			};
		}

		if (workCategoryDismissState.packagingChangeDate !== bannerMeta.data.packagingChangeDate) {
			deleteBannerDismissState({ accountId });

			return {
				...bannerMeta,
				refresh,
			};
		}

		if (workCategoryDismissState.bannerHiddenUntil > Date.now()) {
			return {
				show: false,
			} as const;
		}

		return {
			...bannerMeta,
			...bannerDismissState,
			refresh,
		};
	}, [data, loading, error, workCategory, accountId, refresh]);
};
